/**
 * WorkspaceFormationGatePane — Read-only formation gate and proof kernel inspector
 *
 * Combines 'formation-gate' and 'proof-kernel' panel concepts from UnifiedWorkspace.
 * Surfaces admission decisions (gate entries), lemma/coverage receipts (proof kernel),
 * formation receipt refs, closeout refs, and verification refs.
 *
 * Shell B migration v1: replaces FormationGatePanel.tsx (Tailwind/real data) and
 * inline proof-kernel render (Tailwind/iframe) with OperationalPaneModel shape.
 *
 * Note: The proof-kernel iframe (/formal-proof-kernel.html) is NOT included here.
 * This pane provides receipt-level visibility only. The iframe is a static asset
 * and does not belong in the pane model.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Admission decisions shown here are historical records, not active controls.
 */

import React from 'react';
import type {
  FormationGatePaneModel,
  FormationGateEntry,
  ProofKernelEntry,
  FormationGateDecision,
} from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceFormationGatePaneProps {
  model: FormationGatePaneModel;
  onSelectGateEntry?: (entryId: string) => void;
}

function decisionColor(decision: FormationGateDecision) {
  if (decision === 'ADMITTED') return SEMANTIC.stabilized;
  if (decision === 'REJECTED' || decision === 'BLOCKED') return SEMANTIC.refusal;
  return SEMANTIC.neutral;
}

function decisionIcon(decision: FormationGateDecision): string {
  if (decision === 'ADMITTED') return '●';
  if (decision === 'REJECTED') return '▪';
  if (decision === 'BLOCKED') return '◆';
  return '○';
}

function outcomeColor(outcome: string) {
  if (outcome === 'PASS' || outcome === 'pass') return SEMANTIC.stabilized;
  if (outcome === 'FAIL' || outcome === 'fail') return SEMANTIC.refusal;
  return SEMANTIC.neutral;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
      marginTop: SPACING.sp3,
    }}>
      {title}
    </div>
  );
}

function GateEntryRow({ entry, onSelect }: { entry: FormationGateEntry; onSelect?: (id: string) => void }) {
  const color = decisionColor(entry.decision);
  return (
    <div
      onClick={() => onSelect?.(entry.entryId)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sp2,
        padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
        backgroundColor: entry.isSelected ? NEUTRAL.bg500 : NEUTRAL.bg700,
        borderRadius: 2,
        borderLeft: `2px solid ${entry.isSelected ? color.border : 'transparent'}`,
        cursor: onSelect ? 'pointer' : 'default',
        marginBottom: SPACING.sp1,
      }}
      data-testid={`gate-entry-${entry.entryId}`}
    >
      <span style={{ color: color.marker, fontSize: TYPOGRAPHY.label.size, flexShrink: 0 }}>
        {decisionIcon(entry.decision)}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 700,
        color: color.chipText,
        backgroundColor: color.chip,
        padding: '1px 5px',
        borderRadius: 2,
        minWidth: 64,
        textAlign: 'center' as const,
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3,
        flexShrink: 0,
      }}>
        {entry.decision}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text300,
        minWidth: 72,
        flexShrink: 0,
      }}>
        {entry.timestamp.slice(11, 23)}
      </span>
      <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.boundaryType}
        {entry.reason && ` — ${entry.reason}`}
      </span>
    </div>
  );
}

function ProofKernelRow({ entry }: { entry: ProofKernelEntry }) {
  const color = outcomeColor(entry.outcome);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sp2,
      padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      marginBottom: SPACING.sp1,
    }}>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 700,
        color: color.chipText,
        backgroundColor: color.chip,
        padding: '1px 5px',
        borderRadius: 2,
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3,
        minWidth: 40,
        textAlign: 'center' as const,
        flexShrink: 0,
      }}>
        {entry.outcome.slice(0, 4)}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text300,
        minWidth: 56,
        flexShrink: 0,
      }}>
        {entry.receiptType}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text400,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {entry.receiptHash.slice(0, 16)}… · {entry.receiptRef}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text300,
        flexShrink: 0,
      }}>
        {entry.timestamp.slice(11, 23)}
      </span>
    </div>
  );
}

export const WorkspaceFormationGatePane: React.FC<WorkspaceFormationGatePaneProps> = ({ model, onSelectGateEntry }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Formation gate pane error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Formation Gate
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.totalAdmitted} admitted · {model.totalRejected} rejected · {model.totalPending} pending
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NO EXECUTION AUTHORITY', 'HISTORICAL RECORD ONLY'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: SPACING.sp2,
          marginBottom: SPACING.sp3,
        }}>
          {[
            { label: 'Admitted', value: model.totalAdmitted, color: SEMANTIC.stabilized.text },
            { label: 'Rejected', value: model.totalRejected, color: model.totalRejected > 0 ? SEMANTIC.refusal.text : NEUTRAL.text400 },
            { label: 'Pending', value: model.totalPending, color: NEUTRAL.text400 },
          ].map(stat => (
            <div key={stat.label} style={{
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              padding: `${SPACING.sp2}px`,
              textAlign: 'center' as const,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Gate entries */}
        <SectionHeader title={`Gate Entries (${model.gateEntries.length})`} />
        {model.gateEntries.length === 0 ? (
          <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>No gate entries</div>
        ) : (
          <div>
            {model.gateEntries.map(e => <GateEntryRow key={e.entryId} entry={e} onSelect={onSelectGateEntry} />)}
          </div>
        )}

        {/* Proof kernel */}
        {model.proofKernelEntries.length > 0 && (
          <>
            <SectionHeader title={`Proof Kernel (${model.proofKernelEntries.length})`} />
            <div>
              {model.proofKernelEntries.map(e => <ProofKernelRow key={e.entryId} entry={e} />)}
            </div>
          </>
        )}

        {/* Ref lists */}
        {model.formationReceiptRefs.length > 0 && (
          <>
            <SectionHeader title={`Formation Receipts (${model.formationReceiptRefs.length})`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.formationReceiptRefs.map((r, i) => (
                <div key={i} style={{ fontSize: TYPOGRAPHY.micro.size, fontFamily: TYPOGRAPHY.mono.family, color: NEUTRAL.text400, backgroundColor: NEUTRAL.bg700, padding: `${SPACING.sp1}px ${SPACING.sp2}px`, borderRadius: 2 }}>
                  {r}
                </div>
              ))}
            </div>
          </>
        )}

        {model.closeoutRefs.length > 0 && (
          <>
            <SectionHeader title={`Closeout Refs (${model.closeoutRefs.length})`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.closeoutRefs.map((r, i) => (
                <div key={i} style={{ fontSize: TYPOGRAPHY.micro.size, fontFamily: TYPOGRAPHY.mono.family, color: NEUTRAL.text400, backgroundColor: NEUTRAL.bg700, padding: `${SPACING.sp1}px ${SPACING.sp2}px`, borderRadius: 2 }}>
                  {r}
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
};
